import { createSignal } from "solid-js";
import { A, Location, RouteSectionProps, useNavigate } from "@solidjs/router";
import { LatLng } from "leaflet";

import { BackIcon, QrCodeIcon } from "../../components/icons";
import { WifiCode } from "../../helpers/qr-code";
import LocationPicker from "../../components/location-picker";
import { WifiNetworkFactory } from "../../factories/wifi-network";
import { publish } from "../../services/pool";
import { toastOperation } from "../../helpers/toast";
import { WIFI_SECURITY_TYPES } from "../../const";
import { accounts } from "../../services/accounts";

function CreateWifiView(props: RouteSectionProps) {
  const location: Location<{
    wifi?: WifiCode;
    center?: LatLng;
    zoom?: number;
  }> = props.location;

  const [wifiLocation, setWifiLatlng] = createSignal<LatLng | undefined>();
  const [ssid, setSsid] = createSignal(location.state?.wifi?.ssid ?? "");
  const [security, setSecurity] = createSignal(
    location.state?.wifi?.security ?? WIFI_SECURITY_TYPES.WPA2,
  );
  const [password, setPassword] = createSignal(
    location.state?.wifi?.password ?? "",
  );
  const [hidden, setHidden] = createSignal(
    location.state?.wifi?.hidden ?? false,
  );
  const [captive, setCaptive] = createSignal(false);

  const navigate = useNavigate();

  const save = toastOperation(
    async (event: SubmitEvent) => {
      event.preventDefault();

      const location = wifiLocation();
      if (!location) throw new Error("Location is missing");

      const signed = await WifiNetworkFactory.create(
        {
          ssid: ssid(),
          security: security(),
          password: password(),
          captive: captive(),
          hidden: hidden(),
        },
        location,
      ).sign(accounts.signer);

      await publish(signed);
      navigate("/");
    },
    {
      loading: "Saving WiFi...",
      success: "WiFi created",
      error: (err) => `Error creating WiFi: ${err.message}`,
    },
  );

  return (
    <>
      <main class="flex-col flex-grow flex overflow-auto">
        <LocationPicker
          onPick={setWifiLatlng}
          center={location.state?.center}
          zoom={location.state?.zoom}
        />
        <form id="wifi-network" class="space-y-3 p-4" on:submit={save.run}>
          <label class="form-control w-full" for="ssid">
            <div class="label">
              <span class="label-text">SSID</span>
            </div>
            <input
              id="ssid"
              type="text"
              class="input input-bordered w-full"
              value={ssid()}
              onInput={(e) => setSsid(e.currentTarget.value)}
              disabled={save.loading()}
              required
            />
          </label>
          <label class="form-control w-full" for="password">
            <div class="label">
              <span class="label-text">Password</span>
            </div>
            <input
              id="password"
              type="text"
              class="input input-bordered w-full"
              value={password()}
              onInput={(e) => setPassword(e.currentTarget.value)}
              disabled={save.loading()}
              required={security() !== "nopass"}
            />
          </label>
          <label class="form-control w-full" for="security">
            <div class="label">
              <span class="label-text">Security Type</span>
            </div>
            <select
              id="security"
              class="select select-bordered w-full"
              value={security()}
              onChange={(e) => setSecurity(e.currentTarget.value)}
              disabled={save.loading()}
              required
            >
              {Object.entries(WIFI_SECURITY_TYPES).map(([key, value]) => (
                <option value={key}>{value}</option>
              ))}
            </select>
          </label>
          <label class="label cursor-pointer justify-start gap-3" for="hidden">
            <input
              id="hidden"
              type="checkbox"
              class="checkbox checkbox-primary"
              checked={hidden()}
              onChange={(e) => setHidden(e.currentTarget.checked)}
              disabled={save.loading()}
            />
            <span class="label-text">Hidden network</span>
          </label>
          <label class="label cursor-pointer justify-start gap-3" for="captive">
            <input
              id="captive"
              type="checkbox"
              class="checkbox checkbox-primary"
              checked={captive()}
              onChange={(e) => setCaptive(e.currentTarget.checked)}
              disabled={save.loading()}
            />
            <span class="label-text">Captive portal</span>
          </label>
        </form>
      </main>

      <footer class="bg-primary text-primary-content p-2 pb-safe-or-2 flex items-center gap-2">
        <button
          class="btn btn-ghost btn-square"
          onClick={() => navigate(-1)}
          aria-label="back"
        >
          <BackIcon />
        </button>
        <div class="flex-grow"></div>
        <A
          class="btn btn-ghost btn-square"
          href="/scan"
          aria-label="scan qr code"
        >
          <QrCodeIcon />
        </A>
        <button
          type="submit"
          form="wifi-network"
          class="btn btn-primary"
          disabled={save.loading()}
          aria-label="save network"
        >
          {save.loading() ? "Saving..." : "Save"}
        </button>
      </footer>
    </>
  );
}

export default CreateWifiView;
